import { LTButton, LTText } from '@lambdatestincprivate/lt-components';
import styles from './states.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var format = function format(n) {
  return typeof n === 'number' ? "".concat(Math.round(n / (1024 * 1024)), " MB") : 'unknown size';
};

/** Render a detail.entries list for the gh-artifact advice, capped at 10. */
var listEntries = function listEntries(entries) {
  if (!Array.isArray(entries) || entries.length === 0) return 'no entries';
  var names = entries.filter(function (e) {
    return typeof e === 'string';
  });
  var shown = names.slice(0, 10).join(', ');
  var more = names.length - 10;
  return more > 0 ? "".concat(shown, " \u2026and ").concat(more, " more") : shown;
};

/** One exhaustive code → remediation map. No message sniffing. */
function adviceFor(error) {
  var _error$detail2, _error$detail3, _error$detail$found, _error$detail4, _error$detail5, _error$detail8, _error$detail9, _error$detail0, _error$detail1;
  var code = error.code;
  switch (code) {
    case 'fetch-failed':
      return {
        heading: "Couldn't fetch the pack",
        body: 'The pack URL did not respond or blocked the request (CORS). The ' + 'host must allow this origin and expose Range headers ' + '(Access-Control-Expose-Headers: Content-Range, Accept-Ranges, ' + 'Content-Length). kane-cli’s local server does this automatically.'
      };
    case 'http-error':
      {
        var _error$detail;
        var status = (_error$detail = error.detail) === null || _error$detail === void 0 ? void 0 : _error$detail.status;
        if (status === 403) {
          return {
            heading: 'Pack link expired',
            body: 'The URL returned 403 — a signed link has likely expired. Reload ' + 'the page that embedded this viewer to mint a fresh link.'
          };
        }
        if (status === 404) {
          return {
            heading: 'Pack not found',
            body: 'The URL returned 404. Check the link or re-share it.'
          };
        }
        return {
          heading: "The pack host answered HTTP ".concat(String(status !== null && status !== void 0 ? status : '?')),
          body: 'The pack could not be fetched. Check the URL and try again.'
        };
      }
    case 'size-unavailable':
      return {
        heading: "Can't size the pack",
        body: 'The host hides Content-Range from cross-origin readers and HEAD ' + 'returned no Content-Length. Add Content-Range to the storage ' + "account's CORS ExposedHeaders (the setting everyone forgets)."
      };
    case 'pack-too-large':
      return {
        heading: 'Pack too large to stream from this host',
        body: "This host ignores Range requests, and the pack (".concat(format((_error$detail2 = error.detail) === null || _error$detail2 === void 0 ? void 0 : _error$detail2.size), ") exceeds the ").concat(format((_error$detail3 = error.detail) === null || _error$detail3 === void 0 ? void 0 : _error$detail3.limit), " whole-download limit. Host it on storage with Range support ") + '(S3, Azure Blob, kane-cli) or open the file locally instead.'
      };
    case 'not-a-zip':
      return {
        heading: 'Not an evidence pack',
        body: "This file isn't a sealed .evidence archive (no zip directory " + 'found). A live, unsealed pack directory must be finalized first.'
      };
    case 'not-a-pack':
      return {
        heading: 'Not an evidence pack',
        body: 'The archive has no readable top-level run.yaml with evidence/' + 'run_id — it may be truncated or produced by an incompatible tool.'
      };
    case 'unsupported-version':
      return {
        heading: 'Unsupported evidence version',
        body: "This viewer reads evidence 0.x; the pack declares \"".concat(String((_error$detail$found = (_error$detail4 = error.detail) === null || _error$detail4 === void 0 ? void 0 : _error$detail4.found) !== null && _error$detail$found !== void 0 ? _error$detail$found : '?'), "\". A newer viewer is required.")
      };
    case 'unsupported-compression':
    case 'corrupt-entry':
    case 'entry-too-large':
      return {
        heading: "Couldn't read the pack",
        body: error.message
      };
    case 'aborted':
      return {
        heading: 'Loading cancelled',
        body: ''
      };
    case 'gh-network-error':
      return {
        heading: "Couldn't reach GitHub",
        body: 'The request to api.github.com did not get through — check your ' + 'network/VPN or an ad-blocker, then retry.'
      };
    case 'gh-auth-required':
      return {
        heading: 'GitHub sign-in required',
        body: 'Your GitHub connection is missing or expired. Reload the page ' + 'to connect your GitHub account and open the pack.'
      };
    case 'gh-sso-required':
      return {
        heading: 'Organization single sign-on required',
        body: "This repository's organization protects access with SAML SSO. " + 'Open GitHub, authorize the organization for your account ' + '(the "single sign-on" prompt on the organization page), then ' + 'retry this link.' + (typeof ((_error$detail5 = error.detail) === null || _error$detail5 === void 0 ? void 0 : _error$detail5.ssoUrl) === 'string' ? " Authorize at: ".concat(error.detail.ssoUrl) : '')
      };
    case 'gh-org-approval-required':
      {
        var _error$detail6;
        var org = typeof ((_error$detail6 = error.detail) === null || _error$detail6 === void 0 ? void 0 : _error$detail6.owner) === 'string' ? error.detail.owner : "this repository's organization";
        return {
          heading: 'Organization approval required',
          body: "The \"".concat(org, "\" organization restricts third-party OAuth apps, so ") + 'GitHub blocked this app from reading its artifacts. Ask a ' + "\"".concat(org, "\" organization owner to approve the \"Kane-cli evidence ") + 'viewer" app (GitHub → the organization’s Settings → ' + 'Third-party Access → OAuth app policy), then reload and retry ' + 'this link.'
        };
      }
    case 'gh-artifact-error':
      {
        var _error$detail7;
        var _status = (_error$detail7 = error.detail) === null || _error$detail7 === void 0 ? void 0 : _error$detail7.status;
        if (_status === 410) {
          return {
            heading: 'GitHub artifact expired',
            body: 'Actions artifacts are kept for a limited retention window ' + '(typically 30–90 days), and this one is gone. Re-run the ' + 'workflow to publish a fresh evidence pack.'
          };
        }
        if (_status === 404) {
          return {
            heading: 'GitHub artifact not found',
            body: 'The artifact was deleted, or your GitHub account does not ' + 'have access to this repository (GitHub hides private repos ' + 'as 404). Ask for repository access on GitHub, or check the ' + 'owner/repo/artifact-id in the link.'
          };
        }
        if (_status === 403) {
          return {
            heading: 'GitHub refused the request',
            body: 'Your GitHub token may lack the scope for this repository, or ' + 'GitHub rate-limited the request. Retry in a minute; if it ' + 'persists, reconnect GitHub from a fresh page load.'
          };
        }
        if (_status === 429) {
          return {
            heading: 'Rate limited',
            body: 'GitHub rate-limited the artifact request. Retry shortly.'
          };
        }
        return {
          heading: "GitHub answered HTTP ".concat(String(_status !== null && _status !== void 0 ? _status : '?')),
          body: 'The artifact could not be fetched. Check the link and retry.'
        };
      }
    case 'gh-no-evidence-entry':
      return {
        heading: 'No evidence pack in this artifact',
        body: 'The artifact contains no .evidence file. It holds: ' + listEntries((_error$detail8 = error.detail) === null || _error$detail8 === void 0 ? void 0 : _error$detail8.entries) + '. Point the link at the evidence artifact of the run.'
      };
    case 'gh-multiple-evidence-entries':
      return {
        heading: 'Artifact contains multiple evidence packs',
        body: 'The producer contract is exactly one .evidence per artifact, ' + 'but this one holds: ' + listEntries((_error$detail9 = error.detail) === null || _error$detail9 === void 0 ? void 0 : _error$detail9.entries) + '. Upload one pack per artifact (one artifact per run).'
      };
    case 'gh-artifact-too-large':
      return {
        heading: 'Artifact too large',
        body: "The artifact zip is ".concat(format((_error$detail0 = error.detail) === null || _error$detail0 === void 0 ? void 0 : _error$detail0.size), " \u2014 over the ") + "".concat(format((_error$detail1 = error.detail) === null || _error$detail1 === void 0 ? void 0 : _error$detail1.limit), " download cap for GitHub artifacts.")
      };
    default:
      {
        var exhaustive = code;
        return {
          heading: 'Something went wrong',
          body: String(exhaustive)
        };
      }
  }
}
export function ErrorScreen(_ref) {
  var error = _ref.error,
    onReset = _ref.onReset;
  var advice = adviceFor(error);
  return /*#__PURE__*/_jsx("div", {
    className: styles.wrap,
    role: "alert",
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.card,
      children: [/*#__PURE__*/_jsx("span", {
        className: styles.alertIcon,
        "aria-hidden": "true",
        children: "!"
      }), /*#__PURE__*/_jsx(LTText, {
        as: "h1",
        text: advice.heading,
        color: "var(--lt-text-primary)",
        className: styles.heading,
        style: {
          fontSize: '20px',
          lineHeight: '28px',
          fontWeight: 600
        }
      }), advice.body && /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: advice.body,
        variant: LTText.TextVariant.SUBHEADER_REGULAR,
        color: "var(--lt-text-secondary)",
        className: styles.body
      }), /*#__PURE__*/_jsx(LTText, {
        as: "p",
        text: error.message,
        variant: LTText.TextVariant.SMALL_REGULAR,
        color: "var(--lt-text-muted)",
        className: styles.detail
      }), /*#__PURE__*/_jsx(LTButton, {
        label: "Try a local file instead",
        variant: LTButton.Variant.DEFAULT,
        size: LTButton.Size.MEDIUM,
        onClick: onReset,
        style: {
          marginTop: '8px'
        }
      })]
    })
  });
}